import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useListQuery,
  useNavigateFromList,
  usePageClamp,
} from '@/hooks/useUrlListState';
import { listDefaults, nextSortDirection } from '@/utils/urlListState';
import {
  Pagination,
  UniversalFilter,
  ActionDropdown,
  ActionDropdownAction,
  DateRangeFilter,
  ColumnSettings,
  ResetFiltersButton,
} from '../../../components';
import { useTableColumns } from '../../../hooks/useTableColumns';
import dayjs from 'dayjs';
import { InputSearch } from '../../../components/Input';
import { Status } from '../../../components/Status';
import { TableData } from '../../../components/Table';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '../../../utils';
import { vehiclesApi, Vehicle } from '../../../services/api/vehicles';
import { getListModel } from '../../../services/api/models';
import { getListOem } from '../../../services/api/oems';
import { getListPitstop, Pitstop } from '../../../services/api/pitstops';
import {
  BUSINESS_UNIT_OPTIONS,
  getBusinessUnitLabel,
} from '../../../types/common';
import { AddVehicleModal } from './OwnedVehicle/';
import ReturnVehicleModal from './ReturnVehicle/ReturnVehicleModal';

// Date range interface for DateRangeFilter
interface DateRange {
  from: Date | null;
  to: Date | null;
}

const RETURNED_FILTER_KEYS = [
  'oemIDs',
  'modelIDs',
  'contractTypes',
  'hubIDs',
  'businessUnits',
] as const;

const RETURNED_LIST_DEFAULTS = listDefaults({
  sort: 'createdAt',
  dir: 'desc' as string | null,
  oemIDs: [] as string[],
  modelIDs: [] as string[],
  contractTypes: [] as string[],
  hubIDs: [] as string[],
  businessUnits: [] as string[],
});

interface ReturnedVehicleTabProps {
  onTotalChange?: (total: number) => void;
  onDownloadParamsChange?: (params: Record<string, any>) => void;
}

const ReturnedVehicleTab: React.FC<ReturnedVehicleTabProps> = ({
  onTotalChange,
  onDownloadParamsChange,
}) => {
  const navigate = useNavigateFromList();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);

  // Search, filters, sort and page live in the URL, so a refresh keeps them.
  const {
    searchTerm,
    currentPage,
    pageSize: itemsPerPage,
    sortField: sortFieldParam,
    sortDirection: sortDirectionParam,
    activeFilters: urlFilters,
    dateRange,
    datePickerSeed,
    controlsKey,
    searchInput,
    setSearchInput,
    setSearchTerm,
    setCurrentPage,
    setFilters,
    setDateRange,
    setSort,
    setPageSize: setItemsPerPage,
    hasFilters,
    resetFilters,
    clampToTotal,
  } = useListQuery(RETURNED_LIST_DEFAULTS, RETURNED_FILTER_KEYS);
  const sortField = sortFieldParam as keyof Vehicle;
  const sortDirection = sortDirectionParam ?? 'desc';
  const [totalItems, setTotalItems] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returningVehicle, setReturningVehicle] = useState<Vehicle | null>(
    null
  );
  // RETURNED is the tab itself, not a user filter, so it never goes in the URL.
  // Note: RETURNED is from HandoverStatus, not VehicleStatus.
  const activeFilters = useMemo<Record<string, any>>(
    () => ({ ...urlFilters, statuses: 'RETURNED' }),
    [urlFilters]
  );
  const [models, setModels] = useState<any[]>([]);
  const [oems, setOEMs] = useState<any[]>([]);
  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);

  // Filter configuration for UniversalFilter
  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-2-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Model',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: any) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'contractTypes',
        title: 'Contract Type',
        icon: 'ri-file-list-3-line',
        multiple: true,
        staticOptions: [
          { key: 'RTO', label: 'Rent to Own', value: 'RTO' },
          { key: 'RENT', label: 'Rent', value: 'RENT' },
        ],
      },
      {
        key: 'hubIDs',
        title: 'Hub Location',
        icon: 'ri-map-pin-line',
        multiple: true,
        dataSource: hubs,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-briefcase-line',
        multiple: true,
        staticOptions: BUSINESS_UNIT_OPTIONS,
      },
    ],
    [oems, models, hubs]
  );

  // API integration
  const fetchVehicles = useCallback(
    async (
      page: number,
      search?: string,
      filters?: Record<string, any>,
      size?: number,
      range?: DateRange
    ) => {
      try {
        setLoading(true);

        // Build API filters including date range
        const apiFilters = { ...filters };
        if (apiFilters?.hubIDs && Array.isArray(apiFilters.hubIDs)) {
          apiFilters.hubIDs = apiFilters.hubIDs.join(',');
        }
        if (
          apiFilters?.businessUnits &&
          Array.isArray(apiFilters.businessUnits)
        ) {
          apiFilters.businessUnits = apiFilters.businessUnits.join(',');
        }

        // We leave contractTypes in apiFilters so it's sent to the backend.
        // If the backend doesn't support it, it ignores it.

        // Add date range filters if provided
        if (range?.from) {
          const year = range.from.getFullYear();
          const month = String(range.from.getMonth() + 1).padStart(2, '0');
          const day = String(range.from.getDate()).padStart(2, '0');
          apiFilters.returnStartDate = `${year}-${month}-${day}`;
        }
        if (range?.to) {
          const year = range.to.getFullYear();
          const month = String(range.to.getMonth() + 1).padStart(2, '0');
          const day = String(range.to.getDate()).padStart(2, '0');
          apiFilters.returnEndDate = `${year}-${month}-${day}`;
        }
        apiFilters.order = 'desc';
        apiFilters.sort = 'returnDate';
        const response = await vehiclesApi.getVehicles(
          page,
          size || itemsPerPage,
          search,
          apiFilters
        );

        setVehicles(response.data || []);
        const total = response.pagination?.total || 0;
        setTotalItems(total);

        // Notify parent component of the total count
        if (onTotalChange) {
          onTotalChange(total);
        }
      } catch (err) {
        const errorMessage = extractApiErrorMessage(
          err,
          'Failed to fetch vehicles'
        );
        showErrorToast('Error', errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage, onTotalChange]
  );

  useEffect(() => {
    fetchVehicles(
      currentPage,
      searchTerm,
      activeFilters,
      itemsPerPage,
      dateRange
    );
  }, [
    currentPage,
    searchTerm,
    activeFilters,
    sortField,
    sortDirection,
    fetchVehicles,
    itemsPerPage,
    dateRange,
  ]);

  // A page restored from the URL can sit past the end once the list shrinks.
  usePageClamp(clampToTotal, totalItems, loading);

  useEffect(() => {
    if (!onDownloadParamsChange) return;

    const params: Record<string, any> = {};

    const apiFilters = { ...activeFilters };
    if (apiFilters?.oemIDs && Array.isArray(apiFilters.oemIDs)) {
      apiFilters.oemIDs = apiFilters.oemIDs.join(',');
    }
    if (apiFilters?.modelIDs && Array.isArray(apiFilters.modelIDs)) {
      apiFilters.modelIDs = apiFilters.modelIDs.join(',');
    }
    if (apiFilters?.contractTypes && Array.isArray(apiFilters.contractTypes)) {
      apiFilters.contractTypes = apiFilters.contractTypes.join(',');
    }
    if (apiFilters?.hubIDs && Array.isArray(apiFilters.hubIDs)) {
      apiFilters.hubIDs = apiFilters.hubIDs.join(',');
    }
    if (apiFilters?.businessUnits && Array.isArray(apiFilters.businessUnits)) {
      apiFilters.businessUnits = apiFilters.businessUnits.join(',');
    }

    if (dateRange?.from) {
      const year = dateRange.from.getFullYear();
      const month = String(dateRange.from.getMonth() + 1).padStart(2, '0');
      const day = String(dateRange.from.getDate()).padStart(2, '0');
      apiFilters.returnStartDate = `${year}-${month}-${day}`;
    }
    if (dateRange?.to) {
      const year = dateRange.to.getFullYear();
      const month = String(dateRange.to.getMonth() + 1).padStart(2, '0');
      const day = String(dateRange.to.getDate()).padStart(2, '0');
      apiFilters.returnEndDate = `${year}-${month}-${day}`;
    }

    onDownloadParamsChange({ ...params, ...apiFilters });
  }, [
    onDownloadParamsChange,
    currentPage,
    itemsPerPage,
    searchTerm,
    activeFilters,
    dateRange,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [actionDropdownOpen]);

  useEffect(() => {
    fetchModels();
    fetchOEMs();
    fetchHubs();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await getListModel({ page: '1', size: '1000' });
      setModels(response.data);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to fetch models'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  const fetchOEMs = async () => {
    try {
      const response = await getListOem({ page: '1', size: '1000' });
      setOEMs(response.data);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(err, 'Failed to fetch OEMs');
      showErrorToast('Error', errorMessage);
    }
  };

  const fetchHubs = async () => {
    try {
      const response = await getListPitstop({
        page: '1',
        size: '10000',
        type: 'HUB',
        hasStaff: true,
      });
      setHubs(response.data || []);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(err, 'Failed to fetch hubs');
      showErrorToast('Error', errorMessage);
    }
  };

  const sortedVehicles = useMemo(() => {
    const sorted = [...vehicles].sort((a, b) => {
      let aValue: any = a[sortField as keyof typeof a];
      let bValue: any = b[sortField as keyof typeof b];

      // Special handling for contractType which lives in the model
      if (sortField === ('contractType' as any)) {
        const modelA = models.find((m) => String(m.ID) === String(a.modelID));
        const modelB = models.find((m) => String(m.ID) === String(b.modelID));
        aValue = modelA?.contractType || '';
        bValue = modelB?.contractType || '';
      }

      if (sortField === 'createdAt' || sortField === 'updatedAt') {
        const aDate = aValue ? new Date(aValue as string).getTime() : 0;
        const bDate = bValue ? new Date(bValue as string).getTime() : 0;
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted;
  }, [vehicles, sortField, sortDirection, models]);

  const handleSort = (field: string, direction?: 'asc' | 'desc' | null) =>
    setSort(
      field,
      nextSortDirection(
        field,
        { field: sortField, direction: sortDirection },
        direction
      )
    );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setReturningVehicle(vehicle);
    setIsReturnModalOpen(true);
    setActionDropdownOpen(null);
  };

  const handleToggleDropdown = useCallback((id: string | number) => {
    setActionDropdownOpen((prev) => (prev === id ? null : id));
  }, []);

  const handleModalSubmit = async (vehicleData: any) => {
    try {
      const userEmail = getUserEmail();

      if (editingVehicle) {
        // Add updatedBy when editing
        const updateData = { ...vehicleData, updatedBy: userEmail };
        await vehiclesApi.updateVehicle(editingVehicle.ID!, updateData);
        showSuccessToast('Success', 'Vehicle updated successfully');
      } else {
        // Add createdBy when creating
        const createData = { ...vehicleData, createdBy: userEmail };
        await vehiclesApi.createVehicle(createData);
        showSuccessToast('Success', 'Vehicle created successfully');
      }

      setIsAddModalOpen(false);
      setEditingVehicle(null);

      fetchVehicles(
        currentPage,
        searchTerm,
        activeFilters,
        itemsPerPage,
        dateRange
      );
    } catch (error) {
      const fallbackMessage = `Failed to ${
        editingVehicle ? 'update' : 'create'
      } vehicle`;
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      showErrorToast('Error', errorMessage);
    }
  };

  const handleReturnSuccess = () => {
    setIsReturnModalOpen(false);
    setReturningVehicle(null);
    fetchVehicles(
      currentPage,
      searchTerm,
      activeFilters,
      itemsPerPage,
      dateRange
    );
  };

  const columns = [
    {
      key: 'platNomor',
      title: 'Plate Number',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-bold">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'oemName',
      title: 'OEM Name',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'modelName',
      title: 'Model Name',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'hubName',
      title: 'Hub Location',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">{value ? value : '-'}</span>
      ),
    },
    {
      key: 'businessUnit',
      title: 'Business Unit',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? (
            getBusinessUnitLabel(value)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },

    {
      key: 'vin',
      title: 'Nomor Rangka/VIN',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'imei',
      title: 'Nomor Mesin',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            value
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },

    {
      key: 'contractType',
      title: 'Contract Type',
      sortable: true,
      render: (_value: any, record: Vehicle) => {
        const model = models.find(
          (m) => String(m.ID) === String(record.modelID)
        );
        const type = model?.contractType;
        if (!type) {
          return <span className="text-gray-400">-</span>;
        }
        return <Status value={type} type="contract" />;
      },
    },
    {
      key: 'contractStartDate',
      title: 'Contract Start Date',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            dayjs(value).format('DD/MM/YYYY')
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'returnDate',
      title: 'Return Date',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            dayjs(value).format('DD/MM/YYYY')
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'returnDuration',
      title: 'Duration',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            value + ' Days'
          ) : (
            <span className="text-[14px] font-normal">0 Days</span>
          )}
        </span>
      ),
    },
    {
      key: 'returnBastURL',
      title: 'Dokumen BAST',
      sortable: true,
      render: (value: string) => {
        if (!value) return '-';
        const url = value.startsWith('http') ? value : `https://${value}`;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[14px] text-blue-600 hover:text-blue-800 underline"
          >
            {value.startsWith('http') ? value : `https://${value}`}
          </a>
        );
      },
    },
    {
      key: 'returnOemRepName',
      title: 'Nama Rep OEM',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'returnOemRepPhone',
      title: 'Nomor Rep OEM',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center' as const,
      render: (_value: any, record: Vehicle, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        const actions: ActionDropdownAction[] = [
          {
            key: 'view',
            label: 'View Detail',
            icon: 'ri-eye-line',
            onClick: () => navigate(`/vehicles/${record.ID || record.id}`),
          },
          {
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => handleEditVehicle(record),
          },
        ];
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for vehicle ${record.platNomor}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Vehicle>({
    storageKey: 'vehicles-returned',
    columns,
  });

  return (
    <>
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              key={controlsKey}
              onDateRangeChange={setDateRange}
              {...datePickerSeed}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={
                activeFilters.statuses === 'RETURNED'
                  ? { ...activeFilters, statuses: undefined }
                  : activeFilters
              }
              onApply={(filters: Record<string, any>) => {
                setFilters(filters);
              }}
              onClear={() => {
                setFilters({});
              }}
            />
            <ResetFiltersButton
              visible={hasFilters()}
              onReset={() => resetFilters()}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading Vehicles...</span>
            </div>
          ) : (
            <TableData
              data={sortedVehicles}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              onRowClick={(record) =>
                navigate(`/vehicles/${record.ID || record.id}`)
              }
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setItemsPerPage(newPageSize);
          fetchVehicles(1, searchTerm, activeFilters, newPageSize, dateRange);
        }}
      />

      <AddVehicleModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingVehicle(null);
        }}
        onSubmit={handleModalSubmit}
        vehicle={editingVehicle}
        models={models}
        oems={oems}
      />

      {returningVehicle && (
        <ReturnVehicleModal
          isOpen={isReturnModalOpen}
          onClose={() => {
            setIsReturnModalOpen(false);
            setReturningVehicle(null);
          }}
          isEditMode={true}
          vehicle={returningVehicle}
          onSuccess={handleReturnSuccess}
        />
      )}
    </>
  );
};

export default ReturnedVehicleTab;
